import React, { useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import qs from 'qs'
import {
    EnrollmentStepWrapper,
    EnrollmentStepFooter,
    ReviewRow,
    ReviewBlock,
    ReviewWrapper,
    ReviewDisclaimer,
    EnrollmentFormRow,
    EnrollmentFormCol,
} from '../styles/Enrollment.styled'
import { getEnrollmentRecord, confirmEnrollment } from '../../api/enrollment'
import { toast } from 'react-toastify'
import { Button } from '../styles/Button.styled'
import { Spinner } from 'react-bootstrap'
import { LoadingRow } from '../loading-row'
import moment from 'moment'
import { RollingTextLoading } from '../rolling-text-loading'
import { Helmet } from 'react-helmet'
import { CheckboxItem } from '../checkbox-item'
import { trackEvent } from '../../analytics'
import { getProviderName } from '../../common/data'

interface Props {
    show: boolean
    enrollmentId: string
    handleContinue: Function
    handleBack: Function
    sign: string
    specialPro: {
        ssn: string
        date_of_birth: string
    }
    handleSSN: Function
    provider_id?: number
}

interface EnrollmentFields {
    billing_address: string
    billing_city: string
    billing_state: string
    billing_zipcode: string
    date_of_birth: string
    email: string
    first_name: string
    last_name: string
    phone: string
    requested_date: string
    service_address: string
    service_city: string
    service_state: string
    service_type: string
    ssn: string
}

export const EnrollmentStepReview: React.FC<Props> = ({
    show,
    enrollmentId,
    handleContinue,
    handleBack,
    sign,
    specialPro,
    handleSSN,
    provider_id,
}) => {
    const { t } = useTranslation('enrollment')
    const location = useLocation()
    const { zipcode } = qs.parse(location.search, { ignoreQueryPrefix: true })
    const [reviewData, setReviewData] = useState<EnrollmentFields | undefined>(undefined)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [ isAgreeRecord, setIsAgreeRecord ] = useState(false);
    const [ isAgreeCredit, setIsAgreeCredit ] = useState(false);
    const [ isAgreeLegal, setIsAgreeLegal ] = useState(false);
    const [ isAgreeDoc, setIsAgreeDoc ] = useState(false);
    const [ isAgreeContact, setIsAgreeContact ] = useState(false);
    const [ isAgree2FA, setIsAgree2FA ] = useState(false);
    const [ showConsentError, setShowConsentError ] = useState<String[]>([]);
    const [ repName, setRepName ] = useState('');
    const isAtlantex = Number(provider_id) === 57;

    useEffect(() => {
        if (enrollmentId && show) {
            const tsPlan = localStorage.getItem('ts_plan')
            if (tsPlan) {
                try {
                    const planObj = JSON.parse(tsPlan)
                    setRepName(getProviderName(planObj.provider_id, planObj.provider_name) || '')
                } catch {
                    setRepName('')
                }
            }

            setLoading(true)
            getEnrollmentRecord(enrollmentId, sign).then((res) => {
                if (res && parseInt(res.status) === 1) {
                    setReviewData(res.response)
                } else {
                    toast.error(res?.message || t('An error occurred'))
                }
                setLoading(false)
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    const clearConsentError = (key: string) => {
        const newShowConsentError = [...showConsentError]
        const pos = showConsentError.indexOf(key)
        if (pos >= 0) {
            newShowConsentError.splice(pos, 1)
            setShowConsentError(newShowConsentError)
        }
    }

    const handleSubmit = () => {
        let newConsentError: String[] = [];
        if (isAtlantex) {
            if (!isAgreeCredit) newConsentError.push('credit');
            if (!isAgreeLegal) newConsentError.push('legal');
            if (!isAgreeDoc) newConsentError.push('doc');
            if (!isAgree2FA) newConsentError.push('2FA');
        } else {
            if (!isAgreeRecord) newConsentError.push('record');
            if (!isAgreeCredit) newConsentError.push('credit');
            if (!isAgreeLegal) newConsentError.push('legal');
            if (!isAgreeDoc) newConsentError.push('doc');
            if (!isAgreeContact) newConsentError.push('contact');
        }

        setShowConsentError(newConsentError);

        if (newConsentError.length > 0) return;
        if (submitting) return;

        setSubmitting(true)
        
        try {
            confirmEnrollment(enrollmentId, specialPro, sign).then((res) => {
                if (res) {
                    if (res.error_code && res.error_code === '500') {
                        setSubmitting(false)
                        trackEvent('enrollment_fail', {
                            reason: 'server_error',
                            error_code: '500',
                        })
                        toast.error(res?.message || t('The enrollment request has been submitted'))
                        return
                    }
                    const { status } = res
                    if (status != null && status !== '') {
                        const errorCode = String(status).split('.')[0]
                        switch (errorCode) {
                            case '0':
                            case '-1':
                                setSubmitting(false)
                                trackEvent('enrollment_fail', {
                                    reason: 'api_error',
                                    status: errorCode,
                                })
                                toast.error(t(res.message || 'An error occurred'))
                                break
                            case '10':
                            case '20':
                            case '30':
                            case '40':
                            case '50':
                                handleSSN(specialPro)
                                handleContinue(res)
                                break
                            default:
                                setSubmitting(false)
                                toast.error(t('An error occurred'))
                        }
                    } else {
                        setSubmitting(false)
                        toast.error(t('An error occurred'))
                    }
                } else {
                    setSubmitting(false)
                    trackEvent('enrollment_fail', {
                        reason: 'empty_response',
                    })
                    toast.error(t('An error occurred'))
                }
            })
        } catch (error) {
            setSubmitting(false)
            trackEvent('enrollment_fail', {
                reason: 'exception',
            })
            toast.error(t('An error occurred'))
        }
    }

    const getContent = () => {
        if (loading || !reviewData)
            return (
                <div className="pt-5 pb-5">
                    <LoadingRow />
                </div>
            )

        const {
            billing_address,
            billing_city,
            billing_state,
            billing_zipcode,
            date_of_birth,
            email,
            first_name,
            last_name,
            phone,
            requested_date,
            service_address,
            service_city,
            service_state,
            service_type,
            ssn,
        } = reviewData

        return (
            <ReviewWrapper className="position-relative">
                <Helmet>
                    <title>Last Step Before Enrolling in the New Plan| TEXAS ENERGY GROUP</title>
                    <meta
                        name="description"
                        content="Review the information to make sure everything looks correct before submitting the application to the selected retail energy provider."
                    />
                </Helmet>
                <h5 className="mb-5">{t('Please double check your enrollment details')}</h5>
                <ReviewBlock>
                    <h6>{t('Service Information')}</h6>
                    <ReviewRow>
                        <label>{t('Service Address')}:</label>
                        <span>{`${service_address} ${service_city} ${service_state}, ${zipcode}`}</span>
                    </ReviewRow>
                    <ReviewRow>
                        <label>{t('Billing Address')}:</label>
                        <span>{`${billing_address} ${billing_city} ${billing_state}, ${billing_zipcode}`}</span>
                    </ReviewRow>
                    <ReviewRow>
                        <label>{t('Service Type')}:</label>
                        <span>{service_type}</span>
                    </ReviewRow>
                    <ReviewRow>
                        <label>{t('Requested Date')}:</label>
                        <span>{moment(requested_date).format('MM/DD/YYYY')}</span>
                    </ReviewRow>
                </ReviewBlock>
                <ReviewBlock>
                    <h6>{t('Customer Information')}</h6>
                    <ReviewRow>
                        <label>{t('Customer Name')}:</label>
                        <span>{`${first_name} ${last_name}`}</span>
                    </ReviewRow>
                    {phone && (
                        <ReviewRow>
                            <label>{t('Phone')}:</label>
                            <span>{phone}</span>
                        </ReviewRow>
                    )}
                    <ReviewRow>
                        <label>{t('Email')}:</label>
                        <span>{email}</span>
                    </ReviewRow>
                    {moment(date_of_birth).isValid() && (
                        <ReviewRow>
                            <label>{t('Date of Birth')}:</label>
                            <span>{moment(date_of_birth).format('MM/DD/YYYY')}</span>
                        </ReviewRow>
                    )}
                    {ssn && (
                        <ReviewRow>
                            <label>{t('SSN')}:</label>
                            <span>*****{ssn.substr(5)}</span>
                        </ReviewRow>
                    )}
                </ReviewBlock>
                <EnrollmentFormRow>
                    <EnrollmentFormCol full>
                        {!isAtlantex && (
                            <>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label={<Trans i18nKey="enrollment:consentSubmitLabel" components={[<br />, <br />]} />}
                                        value={`${isAgreeRecord}`}
                                        checked={isAgreeRecord}
                                        handleClick={() => {
                                            clearConsentError('record')
                                            setIsAgreeRecord(!isAgreeRecord);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('record') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label={<Trans i18nKey="enrollment:consentSubmitLableCredit" values={{rep: repName}} shouldUnescape />}
                                        value={`${isAgreeCredit}`}
                                        checked={isAgreeCredit}
                                        handleClick={() => {
                                            clearConsentError('credit')
                                            setIsAgreeCredit(!isAgreeCredit);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('credit') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label={<Trans i18nKey="enrollment:consentSubmitLableLegal" values={{rep: repName}} shouldUnescape />}
                                        value={`${isAgreeLegal}`}
                                        checked={isAgreeLegal}
                                        handleClick={() => {
                                            clearConsentError('legal')
                                            setIsAgreeLegal(!isAgreeLegal);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('legal') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label={<Trans i18nKey="enrollment:consentSubmitLableDoc" values={{rep: repName}} shouldUnescape />}
                                        value={`${isAgreeDoc}`}
                                        checked={isAgreeDoc}
                                        handleClick={() => {
                                            clearConsentError('doc')
                                            setIsAgreeDoc(!isAgreeDoc);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('doc') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label={<Trans i18nKey="enrollment:consentSubmitLableContact" values={{rep: repName}} shouldUnescape />}
                                        value={`${isAgreeContact}`}
                                        checked={isAgreeContact}
                                        handleClick={() => {
                                            clearConsentError('contact')
                                            setIsAgreeContact(!isAgreeContact);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('contact') >= 0}
                                    />
                                </div>
                            </>
                        )}
                        {isAtlantex && (
                            <>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label="I authorize Atlantex Power to obtain information from my credit report to determine whether a deposit is required."
                                        value={`${isAgreeCredit}`}
                                        checked={isAgreeCredit}
                                        handleClick={() => {
                                            clearConsentError('credit')
                                            setIsAgreeCredit(!isAgreeCredit);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('credit') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label="I certify I am over 18 years of age and legally authorized to change the Retail Electric Provider for the address listed under Service Information. I agree that I have decided to change service from my current REP to Atlantex Power. I affirm that I designated Atlantex Power to perform the tasks necessary to complete a switch for me at this address."
                                        value={`${isAgreeLegal}`}
                                        checked={isAgreeLegal}
                                        handleClick={() => {
                                            clearConsentError('legal')
                                            setIsAgreeLegal(!isAgreeLegal);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('legal') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label="I have read and accept the Terms of Service, EFL, and YRAC, and I agree to receive all contract documents and other written communications electronically from Atlantex Power."
                                        value={`${isAgreeDoc}`}
                                        checked={isAgreeDoc}
                                        handleClick={() => {
                                            clearConsentError('doc')
                                            setIsAgreeDoc(!isAgreeDoc);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('doc') >= 0}
                                    />
                                </div>
                                <div className="checkbox-row">
                                    <CheckboxItem
                                        label="I consent to receive up to 6 two-factor authentication (2FA) messages from Atlantex Power at the phone number provided, including messages sent by auto-dialer, to facilitate validation of my enrollment. Consent is not a condition of purchase. Message & data rates may apply. Message frequency varies. Unsubscribe at any time by replying STOP. Reply HELP for help. For additional information, see our website Terms of Use and Privacy Policy."
                                        value={`${isAgree2FA}`}
                                        checked={isAgree2FA}
                                        handleClick={() => {
                                            clearConsentError('2FA')
                                            setIsAgree2FA(!isAgree2FA);
                                        }}
                                        onlyread={false}
                                        disabled={loading}
                                        error={showConsentError.indexOf('2FA') >= 0}
                                    />
                                </div>
                            </>
                        )}
                    </EnrollmentFormCol>
                </EnrollmentFormRow>
                <EnrollmentStepFooter>
                    <Button variant="secondary" disabled={submitting} onClick={() => handleBack()}>
                        {t('Back')}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => handleSubmit()}
                        data-test-id="cta"
                        disabled={submitting}
                    >
                        <span>{t('Submit')}</span>
                        {submitting && (
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                                className="ms-2"
                            />
                        )}
                    </Button>
                </EnrollmentStepFooter>
                <ReviewDisclaimer>
                    {t(
                        "After you click 'Submit', your information will be sent to the Retail Energy Provider (REP) for their review and approval. A soft check on your credit will be run and used to assist with the decision. Submit now and get your approval within a few seconds!"
                    )}
                </ReviewDisclaimer>
                <RollingTextLoading
                    text={[
                        t('This process may take upto 1 minute', { ns: 'common' }),
                        t(
                            'We are transmitting your information to the Retail Energy Provider(REP)...',
                            { ns: 'common' }
                        ),
                        t('Be patient while REP is reviewing your application', { ns: 'common' }),
                        t('REP is still verifying your information...', { ns: 'common' }),
                        t("We are waiting for REP's approval...", { ns: 'common' }),
                        t('Get the approval in a few seconds', { ns: 'common' }),
                    ]}
                    show={submitting}
                />
            </ReviewWrapper>
        )
    }

    if (!show) return null
    return <EnrollmentStepWrapper>{getContent()}</EnrollmentStepWrapper>
}
